import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, ReloadOutlined, TableOutlined } from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import ParamListView from '../../components/ParamListView';
import { deleteCTFlow, getCTFlows } from '../../services/testingApi';
import { getSelectedProject } from '../../services/authApi';


const { Text } = Typography;

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Test Cases', href: '/addTestCase' },
  { label: 'Table', href: '/viewTestCase' },
];

export default function ZinnextTableView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const project = useMemo(() => getSelectedProject(), []);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deletingId, setDeletingId] = useState(null);


  const loadFlows = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const res = await getCTFlows({
        product_parent_ID: opts.project ?? project?.id ?? '',
        search: opts.search ?? search ?? '',
        limit: opts.pageSize ?? pageSize,
        offset: ((opts.page ?? page) - 1) * (opts.pageSize ?? pageSize),
      });
      setRows(res?.items ?? []);
      setTotal(res?.total ?? 0);
    } catch (error) {
      console.error(error);
      message.error('Unable to load test flows.');
    } finally {
      setLoading(false);
    }
  }, [project?.id, search, page, pageSize]);

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Test Case',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this test case?',
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deletingId === record.id },
      cancelText: 'Cancel',
      async onOk() {
        setDeletingId(record.id);
        try {
          await deleteCTFlow(record.id);
          message.success('Test case deleted successfully.');
          const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
          setPage(nextPage);
          await loadFlows({ page: nextPage });
        } catch (error) {
          console.error('Unable to delete test flow:', error);
          message.error('Unable to delete test case.');
          throw error;
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      if (!project?.id) {
        navigate('/select-project', { replace: true });
        return;
      }
      loadFlows({ page: 1 });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = [
    {
      label: 'Actions',
      value: 'rowActions',
      width: 112,
      fixed: 'left',
      sorter: false,
      render: (_value, record) => (
        <Space size={4} className="zt-row-actions">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/addTestCase?edit=${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
    { label: 'ID', value: 'id', width: 70, fixed: 'left' },
    { label: 'Project', value: 'project', width: 130 },
    { label: 'Module', value: 'module', width: 130 },
    { label: 'Submodule', value: 'submodule', width: 140 },
    { label: 'Step', value: 'step_order', width: 70 },
    { label: 'Action', value: 'actions', width: 140 },
    { label: 'Key', value: 'key', width: 160, ellipsis: true },
    { label: 'Value', value: 'value', width: 200, ellipsis: true },
    { label: 'Expected', value: 'expected', width: 180, ellipsis: true },
    { label: 'Description', value: 'description', width: 180, ellipsis: true },
    {
      label: 'Scenario',
      value: 'scenario',
      width: 110,
      render: (v) => (v === 1 ? <Tag color="green">Positive</Tag> : <Tag color="red">Negative</Tag>),
    },
    {
      label: 'Active',
      value: 'active',
      width: 90,
      render: (v) => (v === 1 ? <Tag color="blue">Active</Tag> : <Tag>Inactive</Tag>),
    },
  ];

  const handleSearch = () => {
    setPage(1);
    loadFlows({ page: 1 });
  };


  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col>
          <AppBreadcrumb items={breadcrumbItems} />
        </Col>
        <Col>
          <Button type="link" onClick={() => navigate('/addTestCase')}>
            Back to Form
          </Button>
        </Col>
      </Row>

      <div className="zt-page zt-page-wide">
        <Card
          className="v1-card"
          title={(
            <Space size={6} className="zt-section-title">
              <TableOutlined />
              <span>Test Flows</span>
            </Space>
          )}
          extra={<Text type="secondary">{total} row(s)</Text>}
        >
          <Space wrap className="zt-toolbar">
            <Input.Search
              allowClear
              className="zt-search-filter"
              placeholder="Search module / key / description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={handleSearch}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadFlows()}>
              Refresh
            </Button>
          </Space>

          <ParamListView
            listName="Test Flows"
            loading={loading}
            fields={fields}
            dataSource={rows}
            current={page}
            pageSize={pageSize}
            total={total}
            onPageChange={(nextPage) => {
              setPage(nextPage);
              loadFlows({ page: nextPage });
            }}
            onPageSizeChange={(nextSize) => {
              setPage(1);
              setPageSize(nextSize);
              loadFlows({ page: 1, pageSize: nextSize });
            }}
            rowSelection={false}
            className="zt-list-plain"
          />
        </Card>
      </div>
    </main>
  );
}


